import React, { useCallback, useEffect, useState } from 'react'
import { adminApi } from '../services/api'
import { Loader, Modal } from '../components/UI'
import { formatDateTimeFa, toFaDigits } from '../utils/format'
import toast from 'react-hot-toast'

const PASSWORD_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%'

/** ساخت رمز عبور تصادفی قوی برای کاربر جدید */
const generatePassword = (length = 12) => {
  let out = ''
  for (let i = 0; i < length; i++) {
    out += PASSWORD_CHARS[Math.floor(Math.random() * PASSWORD_CHARS.length)]
  }
  return out
}

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phone: '',
  role: 'USER' as 'USER' | 'ADMIN',
  sendLoginLink: true
}

/** کارت نمایش لینک ورود ساخته‌شده برای کاربر (قابل کپی / ارسال مجدد) */
const LoginLinkBox: React.FC<{
  link: string
  expiresAt?: string | null
  emailResult?: { sent: boolean; mode: 'smtp' | 'console'; error?: string } | null
}> = ({ link, expiresAt, emailResult }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      toast.success('لینک ورود کپی شد')
    } catch {
      toast.error('کپی خودکار ممکن نشد؛ لینک را دستی انتخاب کنید')
    }
  }

  return (
    <div className="rounded-xl border border-primary-200 bg-primary-50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-bold text-primary-900 text-sm">🔗 لینک ورود اختصاصی</div>
        {emailResult && (
          <span className={`badge ${emailResult.sent ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
            {emailResult.sent ? 'ایمیل ارسال شد ✓' : 'ایمیل ارسال نشد — لینک را دستی بفرستید'}
          </span>
        )}
      </div>

      <div dir="ltr" className="bg-white border border-primary-200 rounded-lg p-2 text-[11px] break-all max-h-24 overflow-y-auto">
        {link}
      </div>

      {expiresAt && (
        <div className="text-[11px] text-gray-600">
          اعتبار تا: {formatDateTimeFa(expiresAt)}
        </div>
      )}

      {emailResult && !emailResult.sent && (
        <div className="text-[11px] text-amber-700">
          {emailResult.mode === 'console'
            ? 'SMTP تنظیم نشده است؛ متن ایمیل در backend/logs/mail-outbox.log ذخیره شد.'
            : `ارسال ایمیل ناموفق بود${emailResult.error ? `: ${emailResult.error}` : ''}`}
        </div>
      )}

      <div className="flex gap-2">
        <button className="btn btn-primary !py-2 !text-xs" onClick={copy}>📋 کپی لینک</button>
        <a href={link} target="_blank" rel="noreferrer" className="btn btn-secondary !py-2 !text-xs">↗ باز کردن لینک</a>
      </div>
    </div>
  )
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [updating, setUpdating] = useState<string | null>(null)

  // ساخت کاربر جدید
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({ ...emptyForm })
  const [saving, setSaving] = useState(false)
  const [created, setCreated] = useState<any | null>(null)

  // لینک ورود / تغییر رمز
  const [linkResult, setLinkResult] = useState<any | null>(null)
  const [linkUser, setLinkUser] = useState<any | null>(null)
  const [passwordUser, setPasswordUser] = useState<any | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [savingPassword, setSavingPassword] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await adminApi.getUsers({ search: search || undefined, limit: 100 })
      if (res.data.success && res.data.data) setUsers(res.data.data.users ?? [])
    } catch {
      toast.error('خطا در دریافت کاربران')
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    load()
  }, [load])

  /** باز کردن فرم افزودن کاربر با رمز پیشنهادی تصادفی */
  const openCreate = () => {
    setForm({ ...emptyForm, password: generatePassword() })
    setCreated(null)
    setCreateOpen(true)
  }

  const closeCreate = () => {
    setCreateOpen(false)
    setCreated(null)
  }

  /** ساخت کاربر توسط مدیر (نام کاربری = ایمیل) و ارسال لینک ورود */
  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const res = await adminApi.createUser({
        email: form.email.trim(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim() || undefined,
        role: form.role,
        sendLoginLink: form.sendLoginLink
      })
      if (res.data.success && res.data.data) {
        setCreated(res.data.data)
        toast.success(res.data.message || 'کاربر ساخته شد')
        setSearch('')
        await load()
      } else {
        toast.error(res.data.message || 'خطا در ساخت کاربر')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.message || 'خطا در ساخت کاربر')
    } finally {
      setSaving(false)
    }
  }

  /** ارسال (یا ارسال مجدد) لینک ورود اختصاصی برای کاربر */
  const sendLoginLink = async (u: any) => {
    setUpdating(u.id)
    try {
      const res = await adminApi.sendUserLoginLink(u.id)
      if (res.data.success && res.data.data) {
        setLinkUser(u)
        setLinkResult(res.data.data)
        toast.success(res.data.message || 'لینک ورود ساخته شد')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'خطا در ساخت لینک ورود')
    } finally {
      setUpdating(null)
    }
  }

  const openPassword = (u: any) => {
    setPasswordUser(u)
    setNewPassword(generatePassword())
  }

  /** تعیین رمز عبور جدید برای کاربر توسط مدیر */
  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!passwordUser) return
    setSavingPassword(true)
    try {
      const res = await adminApi.resetUserPassword(passwordUser.id, newPassword)
      if (res.data.success) {
        toast.success('رمز عبور کاربر تغییر کرد')
        setPasswordUser(null)
        setNewPassword('')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.message || 'خطا در تغییر رمز عبور')
    } finally {
      setSavingPassword(false)
    }
  }

  const toggleActive = async (u: any) => {
    setUpdating(u.id)
    try {
      const res = await adminApi.updateUser(u.id, { isActive: !u.isActive })
      if (res.data.success && res.data.data) {
        setUsers((prev) => prev.map((x) => (x.id === u.id ? res.data.data! : x)))
        toast.success(u.isActive ? 'کاربر غیرفعال شد' : 'کاربر فعال شد')
      }
    } catch {
      toast.error('خطا در تغییر وضعیت')
    } finally {
      setUpdating(null)
    }
  }

  const changeRole = async (u: any, role: string) => {
    setUpdating(u.id)
    try {
      const res = await adminApi.updateUser(u.id, { role })
      if (res.data.success && res.data.data) {
        setUsers((prev) => prev.map((x) => (x.id === u.id ? res.data.data! : x)))
        toast.success('نقش کاربر تغییر کرد')
      }
    } catch {
      toast.error('خطا در تغییر نقش')
    } finally {
      setUpdating(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-gray-900">کاربران</h1>
          <p className="text-sm text-gray-500 mt-1">
            ساخت کاربر جدید، تعیین نام کاربری و رمز عبور و ارسال لینک ورود (فقط مدیر)
          </p>
        </div>
        <button className="btn btn-primary !py-2.5" onClick={openCreate}>
          ➕ افزودن کاربر جدید
        </button>
      </div>

      <input className="input !w-72" placeholder="جستجوی نام، ایمیل یا موبایل…" value={search} onChange={(e) => setSearch(e.target.value)} />

      {loading ? (
        <div className="py-16 flex justify-center"><Loader /></div>
      ) : (
        <div className="card overflow-hidden">
          <div className="table-wrap">
            <table className="table-admin">
              <thead>
                <tr>
                  <th>کاربر</th>
                  <th>موبایل</th>
                  <th>نقش</th>
                  <th>سفارش‌ها</th>
                  <th>تاریخ عضویت</th>
                  <th>وضعیت</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={u.isActive ? '' : 'opacity-50 bg-gray-50'}>
                    <td>
                      <div className="font-bold">{u.firstName} {u.lastName}</div>
                      <div className="text-xs text-gray-400" dir="ltr">{u.email}</div>
                    </td>
                    <td dir="ltr">{u.phone || '—'}</td>
                    <td>
                      <span className={`badge ${u.role === 'ADMIN' ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-700'}`}>
                        {u.role === 'ADMIN' ? 'مدیر' : 'کاربر'}
                      </span>
                    </td>
                    <td>{toFaDigits(u._count?.orders || 0)}</td>
                    <td className="text-xs text-gray-500">{formatDateTimeFa(u.createdAt)}</td>
                    <td>
                      <span className={`badge ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {u.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1 items-center flex-wrap">
                        <button
                          className="btn !py-1 !px-2 !text-xs !bg-primary-600 !text-white"
                          disabled={updating === u.id || !u.isActive}
                          title={u.isActive ? 'ساخت و ارسال لینک ورود' : 'کاربر غیرفعال است'}
                          onClick={() => sendLoginLink(u)}
                        >
                          🔗 لینک ورود
                        </button>
                        <button
                          className="btn !py-1 !px-2 !text-xs !bg-gray-700 !text-white"
                          disabled={updating === u.id}
                          onClick={() => openPassword(u)}
                        >
                          🔑 تغییر رمز
                        </button>
                        <select
                          className="input !w-auto !py-1 !text-xs"
                          value={u.role}
                          disabled={updating === u.id}
                          onChange={(e) => changeRole(u, e.target.value)}
                        >
                          <option value="USER">کاربر</option>
                          <option value="ADMIN">مدیر</option>
                        </select>
                        <button
                          className={`btn !py-1 !px-2 !text-xs ${u.isActive ? '!bg-red-500 !text-white' : '!bg-green-500 !text-white'}`}
                          disabled={updating === u.id}
                          onClick={() => toggleActive(u)}
                        >
                          {u.isActive ? 'غیرفعال' : 'فعال'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ساخت کاربر جدید */}
      <Modal open={createOpen} title={created ? 'کاربر ساخته شد' : 'افزودن کاربر جدید'} onClose={closeCreate} wide>
        {created ? (
          <div className="space-y-4">
            <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-sm text-green-900">
              ✅ کاربر <b>{created.user.firstName} {created.user.lastName}</b> با موفقیت ساخته شد.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-500 mb-1">نام کاربری (ایمیل)</div>
                <div dir="ltr" className="font-bold break-all">{created.user.email}</div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-500 mb-1">رمز عبور</div>
                <div dir="ltr" className="font-bold break-all">{form.password}</div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-500 mb-1">نقش</div>
                <div className="font-bold">{created.user.role === 'ADMIN' ? 'مدیر' : 'کاربر'}</div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-500 mb-1">موبایل</div>
                <div dir="ltr" className="font-bold">{created.user.phone || '—'}</div>
              </div>
            </div>

            {created.loginLink ? (
              <LoginLinkBox
                link={created.loginLink}
                expiresAt={created.loginLinkExpiresAt}
                emailResult={created.email}
              />
            ) : (
              <div className="text-xs text-gray-500">
                لینک ورود ساخته نشد. از دکمه «🔗 لینک ورود» در جدول می‌توانید بعداً برای او بسازید.
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button className="btn btn-secondary" onClick={openCreate}>➕ افزودن کاربر دیگر</button>
              <button className="btn btn-primary" onClick={closeCreate}>بستن</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submitCreate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">نام</label>
                <input className="input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
              </div>
              <div>
                <label className="label">نام خانوادگی</label>
                <input className="input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
              </div>
            </div>

            <div>
              <label className="label">نام کاربری (ایمیل)</label>
              <input type="email" className="input" dir="ltr" placeholder="user@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              <p className="text-[11px] text-gray-500 mt-1">کاربر با همین ایمیل وارد می‌شود.</p>
            </div>

            <div>
              <label className="label">رمز عبور</label>
              <div className="flex gap-2">
                <input type="text" className="input" dir="ltr" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={8} required />
                <button type="button" className="btn btn-secondary whitespace-nowrap" onClick={() => setForm({ ...form, password: generatePassword() })}>
                  🎲 تصادفی
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">حداقل ۸ کاراکتر — این رمز برای کاربر ارسال می‌شود.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">موبایل (اختیاری)</label>
                <input className="input" dir="ltr" placeholder="09123456789" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="label">نقش کاربر</label>
                <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as 'USER' | 'ADMIN' })}>
                  <option value="USER">کاربر فروشگاه</option>
                  <option value="ADMIN">مدیر (دسترسی به پنل مدیریت)</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.sendLoginLink} onChange={(e) => setForm({ ...form, sendLoginLink: e.target.checked })} />
              ساخت لینک ورود و ارسال آن برای کاربر از طریق ایمیل
            </label>

            <div className="flex gap-2 justify-end pt-2">
              <button type="button" className="btn btn-secondary" onClick={closeCreate}>انصراف</button>
              <button className="btn btn-primary disabled:opacity-50" disabled={saving}>
                {saving ? 'در حال ساخت…' : 'ساخت کاربر و ارسال لینک'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* لینک ورود کاربر */}
      <Modal open={!!linkResult} title="لینک ورود اختصاصی کاربر" onClose={() => { setLinkResult(null); setLinkUser(null) }}>
        {linkResult && (
          <div className="space-y-4">
            {linkUser && (
              <div className="text-sm text-gray-700">
                کاربر: <b>{linkUser.firstName} {linkUser.lastName}</b>
                <span className="text-xs text-gray-400" dir="ltr"> ({linkUser.email})</span>
              </div>
            )}
            <LoginLinkBox
              link={linkResult.loginLink}
              expiresAt={linkResult.loginLinkExpiresAt}
              emailResult={linkResult.email}
            />
            <p className="text-[11px] text-gray-500">
              این لینک فقط برای ورود همین کاربر است و پس از پایان اعتبار بی‌اعتبار می‌شود. آن را از طریق ایمیل یا پیام‌رسان برای کاربر بفرستید.
            </p>
            <div className="flex justify-end">
              <button className="btn btn-primary" onClick={() => { setLinkResult(null); setLinkUser(null) }}>بستن</button>
            </div>
          </div>
        )}
      </Modal>

      {/* تغییر رمز عبور کاربر */}
      <Modal open={!!passwordUser} title="تعیین رمز عبور جدید" onClose={() => setPasswordUser(null)}>
        {passwordUser && (
          <form onSubmit={submitPassword} className="space-y-4">
            <div className="text-sm text-gray-700">
              کاربر: <b>{passwordUser.firstName} {passwordUser.lastName}</b>
              <span className="text-xs text-gray-400" dir="ltr"> ({passwordUser.email})</span>
            </div>

            <div>
              <label className="label">رمز عبور جدید</label>
              <div className="flex gap-2">
                <input type="text" className="input" dir="ltr" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={8} required />
                <button type="button" className="btn btn-secondary whitespace-nowrap" onClick={() => setNewPassword(generatePassword())}>
                  🎲 تصادفی
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                پس از تغییر رمز، همه نشست‌های فعال کاربر بسته می‌شود. رمز جدید را به او اطلاع دهید.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <button type="button" className="btn btn-secondary" onClick={() => setPasswordUser(null)}>انصراف</button>
              <button className="btn btn-primary disabled:opacity-50" disabled={savingPassword}>
                {savingPassword ? 'در حال ذخیره…' : 'ذخیره رمز عبور'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}

export default Users